import { Key } from "lucide-react";
import type { FpMessagePart } from "../../../../../schemas/codegen";
import { ExpandableMessageCard, SimpleMessageCard } from "./shared";

interface AddSecretsProps {
  part: Extract<FpMessagePart, { type: "tool-call" }>;
}

type SecretArg = {
  name: string;
  value: string;
};

export function AddSecrets({ part }: AddSecretsProps) {
  // Parse secrets from part.args
  const rawSecrets = part.args?.secrets;
  const secrets = Array.isArray(rawSecrets)
    ? (rawSecrets as SecretArg[]).filter(
        (secret) =>
          secret &&
          typeof secret.name === "string" &&
          typeof secret.value === "string",
      )
    : [];

  const hasValidSecrets = secrets.length > 0;

  // Generate display name based on status and secrets count
  const baseText = hasValidSecrets
    ? `${secrets.length} secret${secrets.length !== 1 ? "s" : ""}`
    : "secret(s)";

  const displayName =
    part.status === "pending"
      ? `Adding ${baseText}`
      : part.status === "complete"
        ? `Added ${baseText}`
        : `Failed to add ${baseText}`;

  // If no valid secrets to show, use appropriate card based on status
  if (!hasValidSecrets) {
    // Use expandable card for errors to show error details
    if (part.status === "error") {
      return (
        <ExpandableMessageCard
          status={part.status}
          result={part.result}
          icon={Key}
          title={displayName}
          className="min-w-0"
          accordionValue="add-secrets-error"
        />
      );
    }

    // Use simple card for non-error states
    return (
      <SimpleMessageCard status={part.status} icon={Key} title={displayName} />
    );
  }

  // If we have secrets, use ExpandableMessageCard to show the redacted values
  return (
    <ExpandableMessageCard
      status={part.status}
      result={part.result}
      icon={Key}
      title={displayName}
      className="min-w-0 mb-0"
      accordionValue="add-secrets"
    >
      <div className="space-y-2">
        <div className="text-sm text-muted-foreground">Secret(s) added:</div>
        <div className="space-y-2">
          {secrets.map((secret) => (
            <div
              key={secret.name}
              className="flex items-center justify-between bg-muted/50 p-3 rounded-md"
            >
              <code className="text-sm font-mono">{secret.name}</code>
              <span className="text-sm font-mono text-muted-foreground tracking-wider">
                ••••••••••••
              </span>
            </div>
          ))}
        </div>
      </div>
    </ExpandableMessageCard>
  );
}
